// src/services/vibePoll.js — a poll on the Vibes feed, in the browser.
//
// 🟢 A CASE-FOR-CASE PORT OF allo-app/services/vibePoll.ts. Same option shape
//    as a chat poll — `{ id, text, votes: [] }` with `totalVotes` and
//    `votedUsers` beside it — so the tally, the delta and the percentage are
//    one idea with three homes rather than three ideas that resemble each other.
//
// 🟥 THE PERCENTAGE RULE IS NOT THE CHAT BUBBLE'S. `PollBubble.jsx` divides by
//    the LEADING option's count, so the winner is always exactly 100% — a bar
//    chart wearing a percent sign. A feed poll divides by the number of PEOPLE
//    who voted, which is the number a reader thinks they are being shown. The
//    two are deliberately different and must not be "unified" by copying one
//    into the other.
//
// 🟥 AND THE VOTE IS A SERVER ROUTE, NOT A TRANSACTION FROM HERE. Every account
//    can READ `posts/{id}`, so a rule permissive enough to let you add your own
//    uid to one option is also permissive enough to let anyone replace the
//    array and erase every vote in it — rules cannot inspect an array of maps
//    element by element. See server/functions/vibePolls.js.
import { auth } from '../config/firebase';

const BACKEND = process.env.REACT_APP_BACKEND_URL
  || 'https://signaling-server-prod-397203671414.us-central1.run.app';

export const MAX_POLL_OPTIONS = 6;
export const MIN_POLL_OPTIONS = 2;

/** Is this post a poll? One test, so no surface can disagree with another. */
export function isPollPost(p) {
  return !!p && p.type === 'poll' && Array.isArray(p.pollOptions) && p.pollOptions.length > 0;
}

/**
 * Is this post's caption just its poll question repeated?
 *
 * 🟥 THE COMPOSER WROTE THE SAME STRING INTO TWO FIELDS — one text box feeding
 *    both the poll's `pollQuestion` and the post's `content`. The card then drew
 *    the caption as ordinary text and the poll block drew the question as its
 *    bold heading: "the question appears twice, one bold font and one normal."
 *
 * 🟢 The composer stops duplicating it, and this heals the polls already posted —
 *    fixing only the composer would leave every existing one showing double for
 *    ever. A poll with a genuinely different caption still shows it.
 *
 * 🟥 Compared trimmed and by prefix, because the two fields came from different
 *    code: the question is trimmed AND capped at 300 characters while the caption
 *    is only trimmed, so an exact match would miss a capped question and call it
 *    a real caption. Twin of allo-app/services/vibePoll.ts — this port is
 *    hand-written, so the two have to be kept in step by hand.
 */
export function pollQuestionIsTheContent(p) {
  if (!isPollPost(p)) return false;
  const content = String((p && p.content) || '').trim();
  if (!content) return false;
  const question = String((p && p.pollQuestion) || '').trim();
  if (!question) return false;
  return content === question || content.slice(0, question.length) === question;
}

/** The options as the renderer wants them, whatever the document contains. */
export function pollOptionsOf(p) {
  return (Array.isArray(p?.pollOptions) ? p.pollOptions : []).map((o, i) => ({
    id: String(o?.id || `i${i}`),
    text: String(o?.text || ''),
    votes: Array.isArray(o?.votes) ? o.votes.filter(Boolean) : [],
  }));
}

/**
 * The option array after one person's vote.
 *
 * 🟥 SINGLE CHOICE CLEARS ME EVERYWHERE ELSE, which is not the same as
 *    "replace my vote": a stale vote left on another option counts the same
 *    person twice, for an answer they have changed their mind about.
 * 🟢 VOTING AGAIN FOR WHAT YOU ALREADY CHOSE TAKES IT BACK.
 */
export function applyDelta(options, optionId, action, uid, multiple) {
  const out = (options || []).map((o) => ({
    id: o.id, text: o.text, votes: Array.isArray(o.votes) ? [...o.votes] : [],
  }));
  for (const o of out) {
    if (o.id === optionId) {
      if (action === 'add') { if (!o.votes.includes(uid)) o.votes.push(uid); }
      else o.votes = o.votes.filter((u) => u !== uid);
    } else if (action === 'add' && multiple !== true) {
      o.votes = o.votes.filter((u) => u !== uid);
    }
  }
  return out;
}

/** 🟥 TWO NUMBERS THAT ARE NOT THE SAME NUMBER: votes cast, and people. */
export function tallyOf(options) {
  const opts = options || [];
  return {
    totalVotes: opts.reduce((n, o) => n + (o.votes?.length || 0), 0),
    votedUsers: Array.from(new Set(opts.flatMap((o) => o.votes || []))),
  };
}

/** Have I voted in this poll at all? */
export function haveIVoted(options, uid) {
  return !!uid && (options || []).some((o) => (o.votes || []).includes(uid));
}

/**
 * Does the live document already agree with my optimistic copy about ME?
 *
 * 🟥 THIS IS THE HANDOVER, AND IT HAS TO BE A COMPARISON RATHER THAN A TIMER.
 *    A poll listens to its own document so somebody else's vote appears without
 *    reloading — but the moment I click, the copy on screen is one I painted
 *    before the server had heard of it. Preferring the live copy immediately
 *    makes my own bar flicker back off; preferring mine forever means the
 *    listener may as well not exist. So mine wins only until the live copy
 *    carries the same answers under my name, and then the live copy takes over.
 *
 * 🟢 IT COMPARES ONLY MY OWN MEMBERSHIP, on purpose. Other people's votes land
 *    between my click and the server's reply constantly; requiring the arrays
 *    to match outright would strand a busy poll on my stale snapshot.
 */
export function agreesOnMe(live, mine, uid) {
  if (!live || !mine || !uid) return false;
  const has = (list, id) => {
    const o = list.find((x) => String(x.id) === id);
    return !!o && (o.votes || []).includes(uid);
  };
  return mine.every((o) => has(live, String(o.id)) === (o.votes || []).includes(uid));
}

/**
 * The options to draw: my optimistic copy while it is still ahead of the
 * server, the live document once it has caught up, and the post as it arrived
 * when there is no listener answer yet.
 */
export function optionsToShow({ local, live, post, uid }) {
  if (local && !agreesOnMe(live, local, uid)) return local;
  if (live) return live;
  return local || pollOptionsOf(post);
}

/**
 * The share of VOTERS who chose each option.
 *
 * 🟢 IT MAY SUM TO MORE THAN 100 ON A MULTIPLE-CHOICE POLL, and that is right:
 *    six in ten wanting A and five in ten wanting B is not a contradiction when
 *    they were allowed to want both.
 */
export function pctOf(option, voterCount) {
  if (!voterCount) return 0;
  return Math.round(((option.votes?.length || 0) / voterCount) * 100);
}

/**
 * The poll fields a composer adds to a post, or null when what was typed is not
 * a poll yet.
 *
 * 🟥 THE SAME SHAPE THE SERVER BUILDS, and that is the whole point of it living
 *    here rather than inline in a composer. `pollOptionsFrom` in
 *    server/functions/adminVibes.js produces this for the admin portal; if a
 *    person's poll and Allo's poll differed by one field, the renderer and the
 *    vote route would work on one of them and quietly misbehave on the other.
 *
 * 🟥 IDS ARE ASSIGNED, NOT POSITIONS. A vote names an option by id; positions
 *    would move everybody's vote if a poll were ever edited, and duplicate ids
 *    would land one vote on two answers.
 *
 * 🟢 THE SAME ANSWER TWICE IS ONE ANSWER. Two identical options split the vote
 *    for a single position and make the percentages read as a race that never
 *    happened.
 */
export function buildPollFields(input, stamp) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(input?.options) ? input.options : []) {
    const text = String((raw && raw.text != null ? raw.text : raw) || '').trim().slice(0, 100);
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: `vopt_${stamp}_${out.length}`, text, votes: [] });
    if (out.length >= MAX_POLL_OPTIONS) break;
  }
  if (out.length < MIN_POLL_OPTIONS) return null;
  const question = String(input?.question || '').trim().slice(0, 300);
  if (!question) return null;
  return {
    type: 'poll',
    pollQuestion: question,
    pollOptions: out,
    pollMultiple: input?.multiple === true,
    totalVotes: 0,
    votedUsers: [],
  };
}

/** Is voting over? An absent or zero `pollClosesAt` means it never closes. */
export function isPollClosed(p) {
  const at = Number(p?.pollClosesAt) || 0;
  return at > 0 && Date.now() > at;
}

/**
 * Cast (or withdraw) a vote.
 *
 * 🟢 THE CALLER PAINTS FIRST AND CALLS THIS SECOND. `applyDelta` gives the
 *    optimistic result; this returns the authoritative one, which the caller
 *    should adopt whether or not it matches — the server derives add-or-remove
 *    from what is stored, so a phone and a browser racing each other converge
 *    instead of toggling forever.
 */
export async function castVote(postId, optionId) {
  try {
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'unauthenticated' };
    const idToken = await user.getIdToken();
    const res = await fetch(`${BACKEND}/api/vibes/poll/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ postId, optionId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) return { ok: false, error: String(data?.error || `http_${res.status}`) };
    return data;
  } catch (e) {
    return { ok: false, error: String(e?.message || 'network') };
  }
}
